// src/pages/builder/GameLibraryPanel.js
//
// The game library: every main game and every bonus round, on one set of
// generic sample questions, in any combination.
//
// Sits between the Topic Library and "Your library" in LayoutBuilder. It
// reads nothing and writes nothing — no Firestore, no scores, no progress,
// no leaderboard entries. Playing here cannot touch a student's record and
// cannot change a saved page.
//
// ===========================================================================
// WHY THIS IS NOT JUST A LIST OF LINKS TO EACH GAME
// ===========================================================================
// Four things stand between "render the component" and "play the game", and
// each one is a reason this file exists rather than a route per game.
//
// 1. ROUTER CONTEXT. CountPop, MatchThreeGame, MemoryMatchGame,
//    MultipleChoice, TalkChoice, DragDropQuiz, TypingQuiz and
//    UniversalMathGame all call useNavigate() and useParams() at the top of
//    the component. Rendered bare they throw immediately; rendered inside the
//    builder's real router they read whatever :subject the /layout-builder
//    URL happens to have, and their Back buttons navigate you out of the
//    builder mid-edit — losing unsaved work on the page above. The stage is
//    mounted in its own MemoryRouter with a synthetic
//    /:subject/:state/:grade/:view/:slug entry, so params resolve and every
//    navigation is contained inside the panel.
//
//    That MemoryRouter cannot simply be dropped in: App.js wraps the whole
//    app in a BrowserRouter and React Router v6 throws on a Router inside a
//    Router. See the long note above PreviewFrame for how the subtree is
//    detached from the app router first.
//
// 2. TWO DIFFERENT COMPLETION CALLBACKS. BonusRouter passes its single
//    onComplete prop through under two different names depending on which
//    branch it takes: `onComplete` for memoryMatch / match3 / countPop /
//    typingQuiz / rhythmTap / puzzleShuffle, and `onBonusComplete` for the
//    diamondTap family and UniversalMathBonus. A harness bound to one name
//    reports "never completed" for half the catalogue, and a game wired to
//    the wrong prop looks identical to a game that never finishes. Both names
//    are bound below and the result panel reports which one actually fired,
//    because that distinction is most of what this panel is for.
//
// 3. INCOMPATIBLE QUESTION SHAPES. See the note on SAMPLES in
//    config/gameRegistry.js. One array cannot feed all of them, so the game
//    and the bonus each draw the shape they declared. This is also why mixing
//    is safe: picking drag-and-drop with a matching bonus does not hand
//    drag-drop data to the matching component.
//
// 4. THE `none` BONUS FIRES DURING RENDER. BonusRouter's "none" branch calls
//    onComplete(0) and returns null in the same pass. Calling setState
//    straight out of that is a React "cannot update a component while
//    rendering a different component" warning, so every completion handler
//    here is deferred by a tick. That also gives the double-fire guard
//    somewhere to sit — several games call onComplete more than once.
//
// ===========================================================================
// WHY THE GAMES ARE LOADED LAZILY
// ===========================================================================
// A component stylesheet in CRA is global from the moment the component is in
// the bundle — the whole point of styles/game-shell.css. Importing eleven
// games at the top of this file would put eleven game stylesheets into the
// builder page for everyone who opens it, whether or not they ever press
// Play. Loaded through dynamic import instead, so nothing game-related enters
// the page until you actually start a game. It stays for the rest of the
// session once loaded, which is why the stage is scoped and, in windowed
// mode, clipped.
// ===========================================================================

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as ReactRouterDOM from "react-router-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";

import BonusRouter from "../../components/quizzes/BonusRouter";
import {
  BONUS_ROUNDS,
  MAIN_GAMES,
  MATH_BONUS,
  MATH_GAMES,
  PLACEHOLDER,
  SAMPLE_KEYS,
  SAMPLE_LABELS,
  UNREACHABLE,
  findBonus,
  findMainGame,
  findMathBonus,
  findMathGame,
  parseMathGameType,
  resolveGameType,
  sampleFor,
  scoreOf,
} from "../../config/gameRegistry";

/* ------------------------------------------------------------------ */
/* Lazy component cache                                                */
/* ------------------------------------------------------------------ */

// React.lazy must be called ONCE per component, not once per render — a fresh
// lazy() on every render is a fresh component type, which unmounts and
// remounts the game on every keystroke elsewhere in the panel. Module-level
// cache, keyed by catalogue id.
const lazyCache = new Map();

const lazyFor = (key, loader) => {
  if (!lazyCache.has(key)) lazyCache.set(key, React.lazy(loader));
  return lazyCache.get(key);
};

const mathGameLoader = () => import("../../components/quizzes/UniversalMathGame");

/* ------------------------------------------------------------------ */
/* Preview frame                                                       */
/* ------------------------------------------------------------------ */

// ---------------------------------------------------------------------------
// WHY THE CONTEXTS BELOW GET RESET BEFORE THE MemoryRouter MOUNTS
// ---------------------------------------------------------------------------
// App.js wraps the entire app in a single <BrowserRouter>, and the builder is
// a route inside it. React Router v6 refuses to mount a Router inside another
// Router — <Router> opens with:
//
//     invariant(!useInRouterContext(), "You cannot render a <Router> inside
//                                       another <Router>. ...")
//
// so a bare <MemoryRouter> here throws the moment the panel renders. (The
// same mistake is live in pages/admin/GameLab.js, which is also mounted
// inside the app Router — that page cannot ever have rendered.)
//
// useInRouterContext() is a single check: `useContext(LocationContext) != null`.
// Setting that context back to null immediately above the MemoryRouter makes
// the subtree look like the top of the tree, so the MemoryRouter mounts and
// then supplies its own Location and Navigation contexts to everything below.
// The games get a real router with the synthetic /:subject/:state/:grade/:view/:slug
// entry, and their navigate() calls move an in-memory history that the browser
// URL never sees — which is the whole point, because otherwise a game's Back
// button navigates the builder away and takes unsaved page edits with it.
//
// RouteContext is reset too, and for a separate reason. <Routes> reads the
// parent matches from it to work out how much of the pathname an ancestor has
// already consumed. Left alone it would inherit the builder route's matches
// and try to match the lab path against the wrong remainder.
//
// These are the UNSAFE_-prefixed exports. The prefix means "not part of the
// stable public API", not "unsound" — they are exported deliberately for
// exactly this case, and have been stable across v6. They are read off a
// namespace import and checked at runtime rather than destructured at the top,
// so a version that drops them degrades to the honest message below instead of
// crashing the whole builder page on import.
const LocationContext = ReactRouterDOM.UNSAFE_LocationContext;
const RouteContext = ReactRouterDOM.UNSAFE_RouteContext;

const CAN_ISOLATE = Boolean(LocationContext && RouteContext);

const EMPTY_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

const LAB_ENTRY = "/science/lab/3rd/educator/__lab__";

/**
 * One MemoryRouter around whatever is playing, isolated from the app router.
 *
 * The catch-all route matters: games navigate on exit, and without it the
 * MemoryRouter renders nothing, which reads as "the panel crashed" rather
 * than "the exit button worked".
 */
// Exported so the Game generator panel can play generated games inside the
// builder with exactly the same router isolation. Nothing else changed.
export const PreviewFrame = ({ children }) => {
  if (!CAN_ISOLATE) {
    return (
      <div className="gml-exited">
        <p>Cannot isolate the game from the app router.</p>
        <p className="gml-exited-sub">
          This build of react-router-dom does not export
          UNSAFE_LocationContext and UNSAFE_RouteContext, so a preview router
          cannot be mounted inside the app&rsquo;s BrowserRouter. Running the
          games anyway would let a game&rsquo;s Back button navigate the
          builder away and lose unsaved edits, so the panel stops here instead.
        </p>
      </div>
    );
  }

  return (
    <RouteContext.Provider value={EMPTY_ROUTE_CONTEXT}>
      <LocationContext.Provider value={null}>
        <MemoryRouter initialEntries={[LAB_ENTRY]}>
          <Routes>
            <Route
              path="/:subject/:state/:grade/:view/:slug"
              element={children}
            />
            <Route
              path="*"
              element={
                <div className="gml-exited">
                  <p>The game navigated away.</p>
                  <p className="gml-exited-sub">
                    That is its exit path working, and it stopped here instead
                    of taking the builder with it. Press Restart to play again.
                  </p>
                </div>
              }
            />
          </Routes>
        </MemoryRouter>
      </LocationContext.Provider>
    </RouteContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

const Chip = ({ on, onClick, children, title }) => (
  <button
    type="button"
    title={title}
    className={`gml-chip ${on ? "is-on" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const ChipGroup = ({ title, hint, items, kind, selected, onPick, labelOf }) => (
  <div className="gml-group">
    <h4>{title}</h4>
    {hint && <p className="gml-hint-sm">{hint}</p>}
    <div className="gml-chips">
      {items.map((item) => (
        <Chip
          key={item.id}
          on={selected.kind === kind && selected.id === item.id}
          onClick={() => onPick({ kind, id: item.id })}
          title={labelOf ? labelOf(item) : undefined}
        >
          {item.label}
        </Chip>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */

const GameLibraryPanel = () => {
  const [open, setOpen] = useState(false);

  const [gameSel, setGameSel] = useState({ kind: "main", id: "multipleChoice" });
  const [bonusSel, setBonusSel] = useState({ kind: "bonus", id: "diamondTap" });

  // Free-text escape hatch. A game type that has just been added to
  // UniversalQuizGame but not yet to the registry can be typed here and
  // played, and a bonus string can be sent to BonusRouter raw — including
  // strings the registry does not know about.
  const [customGameType, setCustomGameType] = useState("multipleChoice");
  const [customGameSample, setCustomGameSample] = useState("answers");
  const [customBonusType, setCustomBonusType] = useState("diamondTap");
  const [customBonusSample, setCustomBonusSample] = useState("answers");
  const [customSlug, setCustomSlug] = useState("__lab__");

  // "idle" -> "game" -> "bonus" -> "done"
  const [phase, setPhase] = useState("idle");
  const [runId, setRunId] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const [gameResult, setGameResult] = useState(null);
  const [bonusResult, setBonusResult] = useState(null);

  // Games are not consistent about firing onComplete exactly once. Without a
  // guard, a double fire advances game -> bonus -> done in one go and the
  // bonus never gets a chance to render.
  const firedRef = useRef({ game: false, bonus: false });

  /* ---------------- resolved selection ---------------- */

  const game = useMemo(() => {
    if (gameSel.kind === "math") {
      const entry = findMathGame(gameSel.id) || MATH_GAMES[0];
      return {
        kind: "math",
        label: entry.label,
        typeString: `${entry.mode}:${entry.value}`,
        mode: entry.mode,
        value: entry.value,
        sample: entry.sample || "generated",
        note: null,
        cacheKey: "__math__",
        load: mathGameLoader,
      };
    }

    if (gameSel.kind === "custom") {
      const math = parseMathGameType(customGameType);
      if (math) {
        return {
          kind: "math",
          label: `Custom — ${customGameType}`,
          typeString: customGameType,
          mode: math.mode,
          value: math.value,
          sample: customGameSample,
          note: null,
          cacheKey: "__math__",
          load: mathGameLoader,
        };
      }

      const match = resolveGameType(customGameType);
      if (!match) {
        return {
          kind: "unsupported",
          label: `Custom — ${customGameType || "(empty)"}`,
          typeString: customGameType,
          sample: customGameSample,
          note: null,
        };
      }

      return {
        kind: "main",
        label: `Custom — ${match.label}`,
        typeString: customGameType,
        sample: customGameSample,
        note: match.note,
        cacheKey: match.id,
        load: match.load,
      };
    }

    const entry = findMainGame(gameSel.id) || MAIN_GAMES[0];
    return {
      kind: "main",
      label: entry.label,
      typeString: entry.gameType,
      sample: entry.sample,
      note: entry.note,
      cacheKey: entry.id,
      load: entry.load,
    };
  }, [gameSel, customGameType, customGameSample]);

  const bonus = useMemo(() => {
    if (bonusSel.kind === "mathBonus") {
      const entry = findMathBonus(bonusSel.id) || MATH_BONUS[0];
      return {
        label: entry.label,
        typeString: entry.bonusRoundType,
        slug: "__lab__",
        sample: entry.sample || "generated",
        note: null,
      };
    }

    if (bonusSel.kind === "custom") {
      return {
        label: `Custom — ${customBonusType || "(empty)"}`,
        typeString: customBonusType,
        slug: customSlug || "__lab__",
        sample: customBonusSample,
        note: null,
      };
    }

    const entry = findBonus(bonusSel.id) || BONUS_ROUNDS[0];
    return {
      label: entry.label,
      typeString: entry.bonusRoundType,
      slug: entry.slug || "__lab__",
      sample: entry.sample,
      note: entry.note,
    };
  }, [bonusSel, customBonusType, customBonusSample, customSlug]);

  /* ---------------- run control ---------------- */

  const stop = useCallback(() => {
    firedRef.current = { game: false, bonus: false };
    setPhase("idle");
    setGameResult(null);
    setBonusResult(null);
    setFullscreen(false);
  }, []);

  const start = useCallback(() => {
    firedRef.current = { game: false, bonus: false };
    setGameResult(null);
    setBonusResult(null);
    setPhase("game");
    setRunId((n) => n + 1);
    // play mode fills the screen and the page underneath can't scroll —
    // scrolling mid-game used to slide the library into view
    setFullscreen(true);
  }, []);

  // Changing either pick mid-run would leave a half-played game on screen
  // attached to a bonus it is no longer paired with, so a new pick stops the
  // run rather than swapping under it.
  const pickGame = useCallback(
    (next) => {
      setGameSel(next);
      stop();
    },
    [stop]
  );

  const pickBonus = useCallback(
    (next) => {
      setBonusSel(next);
      stop();
    },
    [stop]
  );

  // Deferred, for the reason in note 4 at the top of this file. Also guards
  // against a second fire from the same stage.
  const handleGameComplete = useCallback((via, payload) => {
    if (firedRef.current.game) return;
    firedRef.current.game = true;

    setTimeout(() => {
      setGameResult({ via, payload, score: scoreOf(payload) });
      setPhase("bonus");
    }, 0);
  }, []);

  const handleBonusComplete = useCallback((via, payload) => {
    if (firedRef.current.bonus) return;
    firedRef.current.bonus = true;

    setTimeout(() => {
      setBonusResult({ via, payload, score: scoreOf(payload) });
      setPhase("done");
    }, 0);
  }, []);

  // Esc leaves fullscreen. Without this the only way out of a game that has
  // taken the whole viewport is a page reload, which loses the unsaved page
  // in the builder above.
  useEffect(() => {
    if (!fullscreen) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") stop();
    };

    window.addEventListener("keydown", onKey);
    // lock the page behind the game
    const html = document.documentElement;
    const before = { html: html.style.overflow, body: document.body.style.overflow };
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      html.style.overflow = before.html;
      document.body.style.overflow = before.body;
    };
  }, [fullscreen, stop]);

  /* ---------------- stage ---------------- */

  const renderStage = () => {
    if (phase === "idle") {
      return (
        <div className="gml-idle">
          <p className="gml-idle-title">
            {game.label} <span>+</span> {bonus.label}
          </p>
          <p className="gml-idle-sub">
            Press Play. The game runs first on its sample content, then hands
            straight to the bonus round, then shows a combined score — the same
            sequence a student gets.
          </p>
          <button type="button" className="lb-btn" onClick={start}>
            ▶ Play this pair
          </button>
        </div>
      );
    }

    if (phase === "done") {
      const total = (gameResult?.score || 0) + (bonusResult?.score || 0);

      return (
        <div className="gml-done">
          <h4>Run complete</h4>
          <div className="gml-scores">
            <div>
              <span>Game</span>
              <strong>{gameResult?.score ?? 0}</strong>
            </div>
            <div>
              <span>Bonus</span>
              <strong>{bonusResult?.score ?? 0}</strong>
            </div>
            <div className="gml-total">
              <span>Total</span>
              <strong>{total}</strong>
            </div>
          </div>
          <p className="gml-done-note">
            In the real player this is where UniversalQuizGame would write the
            score, the progress record and the leaderboard entry. Nothing was
            written here.
          </p>
          <button type="button" className="lb-btn" onClick={start}>
            ↻ Play again
          </button>
        </div>
      );
    }

    if (phase === "game") {
      if (game.kind === "unsupported") {
        return (
          <div className="gml-exited">
            <p>No registered game matches “{game.typeString}”.</p>
            <p className="gml-exited-sub">
              This is exactly what a student would see: UniversalQuizGame falls
              through its switch and renders “Unsupported quiz type”. Add the
              entry to config/gameRegistry.js and the case to
              games/UniversalQuizGame.js.
            </p>
          </div>
        );
      }

      const questions = sampleFor(game.sample);
      const topic = {
        slug: bonus.slug,
        title: `Library — ${game.label}`,
        description: "Sample content. Not from any state or standard.",
        gameType: game.typeString,
        bonusRoundType: bonus.typeString,
        placeholderImage: PLACEHOLDER,
        videoUrl: "",
        musicUrl: "",
      };

      const Component = lazyFor(game.cacheKey, game.load);

      // Every prop any of the games reads is passed. They ignore what they do
      // not use, and a missing placeholderImage in particular is the
      // difference between a puzzle and an empty grey box.
      const common = {
        questions,
        topic,
        user: { uid: "lab", displayName: "Game Library", firstName: "Game" },
        placeholderImage: PLACEHOLDER,
        onComplete: (payload) => handleGameComplete("onComplete", payload),
        onBonusComplete: (payload) =>
          handleGameComplete("onBonusComplete", payload),
      };

      if (game.kind === "math") {
        return <Component {...common} mode={game.mode} value={game.value} />;
      }

      return <Component {...common} />;
    }

    // phase === "bonus".
    //
    // Deliberately goes through BonusRouter rather than importing the bonus
    // component directly. Rendering BonusDiamonds here would test the
    // component and leave the routing untested, and the routing — the
    // slug-keyed map, the mode:value split, the two callback names — is where
    // the bugs have actually been.
    return (
      <BonusRouter
        slug={bonus.slug}
        bonusRoundType={bonus.typeString}
        questions={sampleFor(bonus.sample)}
        placeholderImage={PLACEHOLDER}
        onComplete={(payload) =>
          handleBonusComplete("BonusRouter onComplete", payload)
        }
      />
    );
  };

  const playing = phase === "game" || phase === "bonus";

  // the pair finished: leave the full screen so the result below shows
  useEffect(() => {
    if (!playing && fullscreen) setFullscreen(false);
  }, [playing, fullscreen]);

  /* ---------------- render ---------------- */

  return (
    <section className="lb-card gml-panel">
      <div className="lb-card-head">
        <span className="lb-step gml-step">🎮</span>
        <div>
          <h2>Game library</h2>
          <p className="lb-hint">
            Every game and every bonus round, in any combination, on generic
            sample content. Pick one of each and play the pair end to end.
            Nothing here reads or writes Firestore — no scores, no progress, no
            leaderboard.
          </p>
        </div>

        <div className="gml-head-actions">
          <button
            type="button"
            className="lb-btn lb-btn-ghost lb-btn-sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {!open && (
        <p className="lb-hint gml-closed">
          Closed by default — the games load only when this is opened and
          played, which keeps their stylesheets out of the builder page.
        </p>
      )}

      {open && (
        <>
          <div className="gml-picker">
            {/* ---------------- game column ---------------- */}
            <div className="gml-col">
              <div className="gml-col-head">
                <h3>1. Pick a game</h3>
                <code>{game.typeString || "—"}</code>
              </div>

              <ChipGroup
                title="Main games"
                items={MAIN_GAMES}
                kind="main"
                selected={gameSel}
                onPick={pickGame}
                labelOf={(item) => item.gameType}
              />

              <ChipGroup
                title="Math games"
                hint={'Reached by a gameType of "mode:value", e.g. mathTimesTable:5'}
                items={MATH_GAMES}
                kind="math"
                selected={gameSel}
                onPick={pickGame}
                labelOf={(item) => `${item.mode}:${item.value}`}
              />

              <div className="gml-group">
                <h4>Custom</h4>
                <p className="gml-hint-sm">
                  Type any gameType string — useful for a game you have just
                  wired up but not yet added to the registry.
                </p>
                <div className="gml-custom">
                  <Chip
                    on={gameSel.kind === "custom"}
                    onClick={() => pickGame({ kind: "custom", id: "custom" })}
                  >
                    Use custom
                  </Chip>
                  <input
                    className="gml-input"
                    value={customGameType}
                    onChange={(e) => {
                      setCustomGameType(e.target.value);
                      pickGame({ kind: "custom", id: "custom" });
                    }}
                    placeholder="gameType"
                  />
                  <select
                    className="gml-input gml-select"
                    value={customGameSample}
                    onChange={(e) => setCustomGameSample(e.target.value)}
                  >
                    {SAMPLE_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ---------------- bonus column ---------------- */}
            <div className="gml-col">
              <div className="gml-col-head">
                <h3>2. Pick a bonus</h3>
                <code>{bonus.typeString || "—"}</code>
              </div>

              <ChipGroup
                title="Bonus rounds"
                hint="diamondTap is not one game — it picks a component from the topic's slug, so the first three differ only by slug."
                items={BONUS_ROUNDS}
                kind="bonus"
                selected={bonusSel}
                onPick={pickBonus}
                labelOf={(item) =>
                  item.slug
                    ? `${item.bonusRoundType} / slug: ${item.slug}`
                    : item.bonusRoundType
                }
              />

              <ChipGroup
                title="Math bonus rounds"
                hint="Note the vocabulary shift: the bonus says timesTable, the main game says mathTimesTable. That inconsistency is real, not a typo here."
                items={MATH_BONUS}
                kind="mathBonus"
                selected={bonusSel}
                onPick={pickBonus}
                labelOf={(item) => item.bonusRoundType}
              />

              <div className="gml-group">
                <h4>Custom</h4>
                <p className="gml-hint-sm">
                  Sent to BonusRouter raw. The slug field is what selects a
                  diamondTap variant, so any slug in bonusRoundMap can be
                  previewed here.
                </p>
                <div className="gml-custom">
                  <Chip
                    on={bonusSel.kind === "custom"}
                    onClick={() => pickBonus({ kind: "custom", id: "custom" })}
                  >
                    Use custom
                  </Chip>
                  <input
                    className="gml-input"
                    value={customBonusType}
                    onChange={(e) => {
                      setCustomBonusType(e.target.value);
                      pickBonus({ kind: "custom", id: "custom" });
                    }}
                    placeholder="bonusRoundType"
                  />
                  <input
                    className="gml-input"
                    value={customSlug}
                    onChange={(e) => {
                      setCustomSlug(e.target.value);
                      pickBonus({ kind: "custom", id: "custom" });
                    }}
                    placeholder="slug"
                  />
                  <select
                    className="gml-input gml-select"
                    value={customBonusSample}
                    onChange={(e) => setCustomBonusSample(e.target.value)}
                  >
                    {SAMPLE_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- run bar ---------------- */}
          <div className="gml-runbar">
            <div className="gml-pair">
              <strong>{game.label}</strong>
              <span className="gml-plus">+</span>
              <strong>{bonus.label}</strong>
            </div>

            <div className="gml-runbar-actions">
              <button
                type="button"
                className="lb-btn lb-btn-sm"
                onClick={start}
              >
                {phase === "idle" ? "▶ Play" : "↻ Restart"}
              </button>
              <button
                type="button"
                className="lb-btn lb-btn-ghost lb-btn-sm"
                onClick={stop}
                disabled={phase === "idle"}
              >
                Stop
              </button>
            </div>
          </div>

          {/* ---------------- meta ---------------- */}
          <div className="gml-meta">
            <div>
              <span className="gml-key">gameType</span>
              <code>{game.typeString || "—"}</code>
            </div>
            <div>
              <span className="gml-key">bonusRoundType</span>
              <code>{bonus.typeString || "—"}</code>
            </div>
            <div>
              <span className="gml-key">slug</span>
              <code>{bonus.slug}</code>
            </div>
            <div>
              <span className="gml-key">game sample</span>
              <code>{SAMPLE_LABELS[game.sample] || game.sample}</code>
            </div>
            <div>
              <span className="gml-key">bonus sample</span>
              <code>{SAMPLE_LABELS[bonus.sample] || bonus.sample}</code>
            </div>
            <div className={`gml-phase is-${phase}`}>
              <span className="gml-key">phase</span>
              <code>{phase}</code>
            </div>
          </div>

          {(game.note || bonus.note) && (
            <div className="gml-notes">
              {game.note && (
                <p>
                  <strong>{game.label}:</strong> {game.note}
                </p>
              )}
              {bonus.note && (
                <p>
                  <strong>{bonus.label}:</strong> {bonus.note}
                </p>
              )}
            </div>
          )}

          {/* ---------------- stage ----------------

              The `key` is what resets a game. Remounting is the only reliable
              way: none of them expose a reset, and several hold board state in
              refs that a prop change will not clear.

              The fullscreen class is applied to this same element rather than
              portalling the stage elsewhere, because moving it in the tree
              would remount it and restart the game you were trying to look
              at. */}
          <div
            className={`gml-stage ${fullscreen ? "is-full" : ""} ${
              playing ? "is-playing" : ""
            }`}
            key={`${gameSel.kind}-${gameSel.id}-${bonusSel.kind}-${bonusSel.id}-${runId}-${phase === "bonus" ? "b" : "g"}`}
          >
            {fullscreen && (
              <button
                type="button"
                className="gml-escape"
                onClick={stop}
              >
                ✕ Stop playing (Esc)
              </button>
            )}
            <PreviewFrame>
              <Suspense
                fallback={<div className="gml-loading">Loading game…</div>}
              >
                {renderStage()}
              </Suspense>
            </PreviewFrame>
          </div>

          {/* ---------------- result ---------------- */}
          <div className={`gml-result ${gameResult || bonusResult ? "is-on" : ""}`}>
            {gameResult || bonusResult ? (
              <>
                {gameResult && (
                  <p>
                    <strong>Game finished</strong> — fired{" "}
                    <code>{gameResult.via}</code>, score{" "}
                    <code>{gameResult.score}</code>
                  </p>
                )}
                {bonusResult && (
                  <p>
                    <strong>Bonus finished</strong> — fired{" "}
                    <code>{bonusResult.via}</code>, score{" "}
                    <code>{bonusResult.score}</code>
                  </p>
                )}
                <details>
                  <summary>Raw payloads</summary>
                  <pre>
                    {JSON.stringify(
                      {
                        game: gameResult?.payload ?? null,
                        bonus: bonusResult?.payload ?? null,
                      },
                      null,
                      2
                    )}
                  </pre>
                </details>
              </>
            ) : (
              <span>
                Nothing has completed yet. If a game finishes on screen but
                nothing appears here, it is calling a completion prop this
                panel is not bound to — worth fixing, because UniversalQuizGame
                would miss it too and the child would never get their score.
              </span>
            )}
          </div>

          {/* ---------------- unreachable ---------------- */}
          <div className="gml-dead-wrap">
            <h4>Built but unreachable</h4>
            <p className="gml-hint-sm">
              These render fine, but no slug in BonusRouter&rsquo;s
              bonusRoundMap points at them, so nothing in the app can currently
              reach them. To bring one back, add a slug key in BonusRouter.js
              and an entry in config/gameRegistry.js.
            </p>
            <ul className="gml-dead">
              {UNREACHABLE.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="gml-howto">
            <strong>Adding a game or bonus:</strong> build the component, add
            one entry to <code>src/config/gameRegistry.js</code>, then add the
            matching <code>case</code> to the switch in{" "}
            <code>games/UniversalQuizGame.js</code> (main games) or{" "}
            <code>components/quizzes/BonusRouter.js</code> (bonus rounds). The
            registry entry is what puts it in this picker and in the Topic
            Library dropdowns at the same time.
          </p>
        </>
      )}

      {/* Every rule is namespaced .gml- AND scoped under `.lb-page .gml-panel`,
          so nothing here can leak into a game, a portal page or any other
          screen — the same discipline as game-shell.css and the topic library
          panel. Buttons reuse .lb-btn and the --lb-* custom properties rather
          than a private palette, so they are the same blue as the rest of the
          builder and stay in step if that palette changes. */}
      <style>{`
        .lb-page .gml-panel { border-color: #cfe6dd; }
        .lb-page .gml-panel .gml-step { background: var(--lb-green); color: #fff; }
        .lb-page .gml-panel .gml-head-actions { display: flex; gap: 8px; flex: none; }
        .lb-page .gml-panel .gml-closed { margin: 0; }

        .lb-page .gml-panel .gml-picker { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 16px; }
        .lb-page .gml-panel .gml-col { border: 1px solid var(--lb-line); border-radius: 14px; padding: 14px 15px; background: var(--lb-tint); }
        .lb-page .gml-panel .gml-col-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
        .lb-page .gml-panel .gml-col-head h3 { margin: 0; font-size: 14px; color: var(--lb-ink); }

        .lb-page .gml-panel .gml-group { margin-bottom: 14px; }
        .lb-page .gml-panel .gml-group:last-child { margin-bottom: 0; }
        .lb-page .gml-panel .gml-group h4 { margin: 0 0 5px; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-hint-sm { margin: 0 0 8px; font-size: 12px; line-height: 1.45; color: var(--lb-ink-soft); }

        .lb-page .gml-panel .gml-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .lb-page .gml-panel .gml-chip { border: 1.5px solid var(--lb-line); background: #fff; color: var(--lb-ink-soft); border-radius: 999px; padding: 6px 13px; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer; }
        .lb-page .gml-panel .gml-chip:hover { background: #fff; border-color: var(--lb-blue); color: var(--lb-blue-dark); }
        .lb-page .gml-panel .gml-chip.is-on { background: var(--lb-blue); border-color: var(--lb-blue); color: #fff; }

        .lb-page .gml-panel .gml-custom { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
        .lb-page .gml-panel .gml-input { flex: 1 1 130px; min-width: 110px; padding: 7px 11px; border: 1.5px solid var(--lb-line); border-radius: 10px; font-family: inherit; font-size: 13px; color: var(--lb-ink); background: #fff; }
        .lb-page .gml-panel .gml-input:focus { outline: none; border-color: var(--lb-blue); }
        .lb-page .gml-panel .gml-select { flex: 0 0 auto; min-width: 104px; }

        .lb-page .gml-panel .gml-runbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; border: 1.5px solid var(--lb-line); border-radius: 14px; padding: 11px 14px; margin-bottom: 12px; }
        .lb-page .gml-panel .gml-pair { display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-plus { color: var(--lb-ink-soft); font-weight: 700; }
        .lb-page .gml-panel .gml-runbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }

        .lb-page .gml-panel .gml-meta { display: flex; flex-wrap: wrap; gap: 8px 18px; margin-bottom: 10px; font-size: 12.5px; }
        .lb-page .gml-panel .gml-key { color: var(--lb-ink-soft); margin-right: 6px; }
        .lb-page .gml-panel code { background: #eef3fa; padding: 2px 7px; border-radius: 5px; font-size: 12px; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-phase.is-game code { background: #e6f0ff; color: var(--lb-blue-dark); }
        .lb-page .gml-panel .gml-phase.is-bonus code { background: #f3ecff; color: #6d3fd4; }
        .lb-page .gml-panel .gml-phase.is-done code { background: #e4f7ef; color: #0d8a67; }

        .lb-page .gml-panel .gml-notes { border: 1px solid #f0e2bd; background: #fdf6e3; border-radius: 10px; padding: 9px 13px; margin-bottom: 12px; }
        .lb-page .gml-panel .gml-notes p { margin: 0 0 5px; font-size: 12.5px; line-height: 1.5; color: #8a6d3b; }
        .lb-page .gml-panel .gml-notes p:last-child { margin-bottom: 0; }

        .lb-page .gml-panel .gml-stage { position: relative; border: 1px solid var(--lb-line); border-radius: 14px; min-height: 520px; overflow: auto; background: #fff; }
        .lb-page .gml-panel .gml-stage.is-full { position: fixed; inset: 0; z-index: 4000; border-radius: 0; border: 0; min-height: 100vh; overscroll-behavior: contain; }
        .lb-page .gml-panel .gml-escape { position: fixed; top: 14px; right: 14px; z-index: 4001; border: 0; border-radius: 999px; padding: 9px 16px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; background: rgba(22,35,63,.82); color: #fff; }

        .lb-page .gml-panel .gml-idle,
        .lb-page .gml-panel .gml-done,
        .lb-page .gml-panel .gml-exited,
        .lb-page .gml-panel .gml-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; min-height: 500px; padding: 40px 24px; text-align: center; }
        .lb-page .gml-panel .gml-idle-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-idle-title span { color: var(--lb-ink-soft); margin: 0 6px; }
        .lb-page .gml-panel .gml-idle-sub,
        .lb-page .gml-panel .gml-exited-sub,
        .lb-page .gml-panel .gml-done-note { margin: 0; max-width: 52ch; font-size: 13px; line-height: 1.6; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-exited p { margin: 0; font-size: 15px; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-loading { color: var(--lb-ink-soft); font-size: 14px; }

        .lb-page .gml-panel .gml-done h4 { margin: 0; font-size: 17px; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-scores { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
        .lb-page .gml-panel .gml-scores > div { border: 1.5px solid var(--lb-line); border-radius: 12px; padding: 10px 20px; min-width: 96px; }
        .lb-page .gml-panel .gml-scores span { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-scores strong { font-size: 24px; color: var(--lb-ink); }
        .lb-page .gml-panel .gml-total { background: #e4f7ef; border-color: #b6e3d1 !important; }

        .lb-page .gml-panel .gml-result { margin-top: 12px; border: 1px dashed var(--lb-line); border-radius: 12px; padding: 11px 14px; font-size: 12.5px; line-height: 1.55; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-result.is-on { border-style: solid; border-color: #b6e3d1; background: #f3fbf7; color: #14624c; }
        .lb-page .gml-panel .gml-result p { margin: 0 0 5px; }
        .lb-page .gml-panel .gml-result summary { cursor: pointer; margin-top: 6px; }
        .lb-page .gml-panel .gml-result pre { margin: 6px 0 0; font-size: 11.5px; white-space: pre-wrap; word-break: break-word; }

        .lb-page .gml-panel .gml-dead-wrap { margin-top: 16px; }
        .lb-page .gml-panel .gml-dead-wrap h4 { margin: 0 0 5px; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-dead { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px 18px; }
        .lb-page .gml-panel .gml-dead li { display: flex; flex-direction: column; font-size: 12px; color: var(--lb-ink-soft); }
        .lb-page .gml-panel .gml-dead strong { color: var(--lb-ink); font-weight: 700; }

        .lb-page .gml-panel .gml-howto { margin: 14px 0 0; font-size: 12.5px; line-height: 1.6; color: var(--lb-ink-soft); }

        @media (max-width: 900px) {
          .lb-page .gml-panel .gml-picker { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
};

export default GameLibraryPanel;
